import { useMutation } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/language-context';
import { updateJobVisit, type JobStatus } from '@/lib/api/jobs';

import { nextStatus } from '../job-detail/helpers';

interface Props {
  businessId: string;
  jobId: string;
  visitId: string;
  status: JobStatus;
}

// Advances a visit one step (draft → started → completed). An assigned
// employee may only set `status`, and only to started/completed — `nextStatus`
// never offers `cancelled`, so this button can't request it. The global
// `MutationCache` (see `lib/query-client.ts`) handles the success toast and
// refetch, so there's no `onSuccess` here.
export function VisitStatusAction({ businessId, jobId, visitId, status }: Props) {
  const { t } = useLanguage();

  const statusMutation = useMutation({
    mutationFn: (next: JobStatus) => updateJobVisit(businessId, jobId, visitId, { status: next }),
    meta: { successMessage: t.jobUpdated },
  });

  const advance = nextStatus(status);
  if (!advance) return null;

  return (
    <Button
      type="button"
      disabled={statusMutation.isPending}
      onClick={() => statusMutation.mutate(advance)}
      data-testid="visit-status-advance"
    >
      {advance === 'started' ? t.seriesAdvanceToStarted : t.seriesAdvanceToCompleted}
    </Button>
  );
}
